import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ArrowLeft, ArrowRight, Shield, Truck, Lock, CreditCard, CheckCircle, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, clearCart, setCurrentOrderId, orderId, getCartTotal } = useCart();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [customerInfo, setCustomerInfo] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    address_line1: '', address_line2: '', suburb: '', state: '', postcode: ''
  });

  const [cardDetails, setCardDetails] = useState({
    name: '', number: '', expiry_month: '', expiry_year: '', cvn: ''
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(price);
  };

  const cartTotals = getCartTotal();

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleCardDetailsChange = (e) => {
    const { name, value } = e.target;
    setCardDetails(prev => ({ ...prev, [name]: value }));
  };

  const validateShippingInfo = () => {
    const required = ['first_name', 'last_name', 'email', 'phone', 'address_line1', 'suburb', 'state', 'postcode'];
    for (const field of required) {
      if (!customerInfo[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validateCardDetails = () => {
    if (!cardDetails.name || !cardDetails.number || !cardDetails.expiry_month || !cardDetails.expiry_year || !cardDetails.cvn) {
      toast.error('Please fill in all card details');
      return false;
    }
    if (cardDetails.number.replace(/\s/g, '').length < 13) {
      toast.error('Please enter a valid card number');
      return false;
    }
    return true;
  };

  const handleProceedToPayment = async () => {
    if (!validateShippingInfo()) return;
    
    setLoading(true);
    try {
      const items = cartItems.map(item => ({
        car: item.car,
        product: {
          product_type: item.product.product_type,
          include_installation_kit: item.product.include_installation_kit,
          colour: item.product.colour
        }
      }));

      const orderData = { items, customer: customerInfo };
      const response = await axios.post(`${API}/orders`, orderData);
      setCurrentOrderId(response.data.id);
      setStep(3);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!validateCardDetails()) return;
    
    setLoading(true);
    try {
      const paymentData = {
        order_id: orderId,
        card_details: {
          name: cardDetails.name,
          number: cardDetails.number.replace(/\s/g, ''),
          expiry_month: cardDetails.expiry_month,
          expiry_year: cardDetails.expiry_year,
          cvn: cardDetails.cvn
        }
      };

      const response = await axios.post(`${API}/payment/process`, paymentData);
      
      if (response.data.success) {
        setPaymentSuccess(true);
        clearCart();
      } else {
        toast.error(response.data.message || 'Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Empty cart view
  if (cartItems.length === 0 && !paymentSuccess) {
    return (
      <div className="min-h-screen pt-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h1 className="text-3xl font-medium text-[#F8F8F8] mb-4">Your Cart is Empty</h1>
          <p className="text-[#A0A0A0] mb-8">Build your custom PPF kit to get started.</p>
          <Button
            onClick={() => navigate('/products')}
            className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-8 py-4 rounded-none"
            data-testid="empty-cart-build-kit-btn"
          >
            Build Your Kit
          </Button>
        </div>
      </div>
    );
  }

  // Payment success view
  if (paymentSuccess) {
    return (
      <div className="min-h-screen pt-24 bg-[#050505]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <div className="w-20 h-20 bg-[#34C759] rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-[#050505]" />
          </div>
          <h1 className="text-3xl font-medium text-[#F8F8F8] mb-4">Payment Successful!</h1>
          <p className="text-[#A0A0A0] mb-2">Thank you for your order.</p>
          <p className="text-[#666666] text-sm mb-8">
            Order confirmation has been sent to your email address. 
            Your custom-cut PPF kit(s) will be dispatched within 3-5 business days.
          </p>
          <div className="bg-[#111111] border border-[#222222] p-6 mb-8">
            <p className="text-sm text-[#A0A0A0] mb-2">Order Reference</p>
            <p className="text-[#00F0FF] font-mono text-lg">{orderId}</p>
          </div>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-8 py-4 rounded-none"
            data-testid="success-home-btn"
          >
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 bg-[#050505]">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-2">Checkout</p>
              <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-[#F8F8F8]">
                {step === 1 && 'Review Order'}
                {step === 2 && 'Shipping Details'}
                {step === 3 && 'Payment'}
              </h1>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`step-indicator ${step === s ? 'active' : step > s ? 'completed' : 'inactive'}`}>{s}</div>
                  {s < 3 && <div className={`w-12 h-0.5 ml-4 ${step > s ? 'bg-[#00F0FF]' : 'bg-[#333333]'}`} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main content */}
          <div className="lg:col-span-2">
            {/* Step 1: Review */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-medium text-[#F8F8F8]">Order Review ({cartItems.length} item{cartItems.length > 1 ? 's' : ''})</h2>
                    <Link to="/products">
                      <Button variant="outline" className="border-[#444444] text-[#F8F8F8] hover:bg-[#1A1A1A] rounded-none" data-testid="add-more-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Another
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="space-y-6">
                    {cartItems.map((item, index) => (
                      <div key={item.cartId} className={`pb-6 ${index < cartItems.length - 1 ? 'border-b border-[#222222]' : ''}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm text-[#A0A0A0] mb-1">Vehicle {cartItems.length > 1 ? `#${index + 1}` : ''}</p>
                            <p className="text-[#F8F8F8] font-medium text-lg">
                              {item.car.year} {item.car.brand} {item.car.model}
                            </p>
                            <p className="text-xs text-[#666666] uppercase mt-1">{item.car.body_type}</p>
                            
                            <div className="mt-3">
                              <p className="text-[#F8F8F8]">{item.product.name}</p>
                              {item.product.colour_name && (
                                <p className="text-[#00F0FF] text-sm">Colour: {item.product.colour_name}</p>
                              )}
                              {item.product.include_installation_kit && (
                                <p className="text-[#A0A0A0] text-sm">+ Installation Kit</p>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2 mt-2">
                              <p className="text-[#00F0FF] font-bold">{formatPrice(item.pricing.product_price + item.pricing.installation_kit_price)}</p>
                              {item.pricing.discount > 0 && (
                                <p className="text-[#666666] line-through text-sm">{formatPrice(item.pricing.original_price + item.pricing.installation_kit_price)}</p>
                              )}
                            </div>
                          </div>
                          <button 
                            onClick={() => removeFromCart(item.cartId)}
                            className="text-[#666666] hover:text-[#FF3B30] transition-colors p-2"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pt-6 border-t border-[#222222]">
                    <div className="flex items-center space-x-2 text-[#A0A0A0]">
                      <Truck className="w-4 h-4" />
                      <span className="text-sm">Australia Post Delivery: {formatPrice(35)}</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] py-6 rounded-none"
                  data-testid="proceed-to-shipping-btn"
                >
                  Proceed to Shipping
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            )}

            {/* Step 2: Shipping */}
            {step === 2 && (
              <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
                <h2 className="text-xl font-medium text-[#F8F8F8] mb-6">Shipping Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#A0A0A0]">First Name *</Label>
                    <Input name="first_name" value={customerInfo.first_name} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-first-name" />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Last Name *</Label>
                    <Input name="last_name" value={customerInfo.last_name} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-last-name" />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Email *</Label>
                    <Input name="email" type="email" value={customerInfo.email} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-email" />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Phone *</Label>
                    <Input name="phone" value={customerInfo.phone} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#A0A0A0]">Address Line 1 *</Label>
                    <Input name="address_line1" value={customerInfo.address_line1} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-address1" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#A0A0A0]">Address Line 2</Label>
                    <Input name="address_line2" value={customerInfo.address_line2} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-address2" />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Suburb *</Label>
                    <Input name="suburb" value={customerInfo.suburb} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-suburb" />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">State *</Label>
                    <Select value={customerInfo.state} onValueChange={(value) => setCustomerInfo(prev => ({ ...prev, state: value }))}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="select-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                        {AUSTRALIAN_STATES.map(state => (
                          <SelectItem key={state.value} value={state.value} className="text-[#F8F8F8]">{state.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Postcode *</Label>
                    <Input name="postcode" value={customerInfo.postcode} onChange={handleCustomerInfoChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-postcode" />
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Button onClick={() => setStep(1)} variant="outline"
                    className="border-[#444444] text-[#F8F8F8] hover:bg-[#1A1A1A] py-6 rounded-none flex-1" data-testid="back-to-review-btn">
                    <ArrowLeft className="mr-2 w-5 h-5" /> Back
                  </Button>
                  <Button onClick={handleProceedToPayment} disabled={loading}
                    className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] py-6 rounded-none flex-1" data-testid="proceed-to-payment-btn">
                    {loading ? 'Processing...' : 'Proceed to Payment'} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Lock className="w-5 h-5 text-[#34C759]" />
                  <h2 className="text-xl font-medium text-[#F8F8F8]">Secure Payment</h2>
                </div>
                
                <div className="flex items-center space-x-2 mb-6 text-sm text-[#A0A0A0]">
                  <CreditCard className="w-4 h-4" />
                  <span>Secured by eWay Payment Gateway</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-[#A0A0A0]">Name on Card *</Label>
                    <Input name="name" value={cardDetails.name} onChange={handleCardDetailsChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-card-name" />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Card Number *</Label>
                    <Input name="number" value={cardDetails.number} onChange={handleCardDetailsChange} placeholder="1234 5678 9012 3456"
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-card-number" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[#A0A0A0]">Expiry Month *</Label>
                      <Select value={cardDetails.expiry_month} onValueChange={(value) => setCardDetails(prev => ({ ...prev, expiry_month: value }))}>
                        <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="select-expiry-month">
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                          {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(month => (
                            <SelectItem key={month} value={month} className="text-[#F8F8F8]">{month}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#A0A0A0]">Expiry Year *</Label>
                      <Select value={cardDetails.expiry_year} onValueChange={(value) => setCardDetails(prev => ({ ...prev, expiry_year: value }))}>
                        <SelectTrigger className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="select-expiry-year">
                          <SelectValue placeholder="YY" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                          {Array.from({ length: 10 }, (_, i) => String(26 + i)).map(year => (
                            <SelectItem key={year} value={year} className="text-[#F8F8F8]">{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#A0A0A0]">CVN *</Label>
                      <Input name="cvn" type="password" maxLength={4} value={cardDetails.cvn} onChange={handleCardDetailsChange} placeholder="123"
                        className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2" data-testid="input-cvn" />
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Button onClick={() => setStep(2)} variant="outline"
                    className="border-[#444444] text-[#F8F8F8] hover:bg-[#1A1A1A] py-6 rounded-none flex-1" data-testid="back-to-shipping-btn">
                    <ArrowLeft className="mr-2 w-5 h-5" /> Back
                  </Button>
                  <Button onClick={handlePayment} disabled={loading}
                    className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] py-6 rounded-none flex-1" data-testid="pay-now-btn">
                    {loading ? 'Processing...' : `Pay ${formatPrice(cartTotals.total)}`} <Lock className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              <div className="bg-[#111111] border border-[#222222] p-6">
                <h3 className="text-lg font-medium text-[#F8F8F8] mb-6">Order Summary</h3>
                
                {cartItems.map((item, index) => (
                  <div key={item.cartId} className={`mb-4 pb-4 ${index < cartItems.length - 1 ? 'border-b border-[#222222]' : ''}`}>
                    <p className="text-sm text-[#A0A0A0] mb-1">{item.car.year} {item.car.brand} {item.car.model}</p>
                    <p className="text-[#F8F8F8] text-sm">{item.product.name}</p>
                    {item.product.colour_name && (
                      <p className="text-[#00F0FF] text-xs">Colour: {item.product.colour_name}</p>
                    )}
                    <p className="text-[#00F0FF] font-bold text-sm mt-1">{formatPrice(item.pricing.product_price + item.pricing.installation_kit_price)}</p>
                  </div>
                ))}

                <div className="space-y-3 mb-6 pt-4 border-t border-[#222222]">
                  <div className="flex justify-between">
                    <span className="text-[#A0A0A0]">Subtotal</span>
                    <span className="text-[#F8F8F8]">{formatPrice(cartTotals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A0A0A0]">Shipping</span>
                    <span className="text-[#F8F8F8]">{formatPrice(cartTotals.shipping)}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center py-4 border-t border-[#222222]">
                  <span className="text-[#F8F8F8] font-medium">Total</span>
                  <span className="text-[#00F0FF] text-2xl font-bold">{formatPrice(cartTotals.total)}</span>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-[#A0A0A0]">
                    <Shield className="w-4 h-4 text-[#34C759]" />
                    <span>10 Year Warranty</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-[#A0A0A0]">
                    <Truck className="w-4 h-4 text-[#00F0FF]" />
                    <span>Australia Post Delivery</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-[#A0A0A0]">
                    <Lock className="w-4 h-4 text-[#34C759]" />
                    <span>Secure Payment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
